/**
 * Виджет «Классификация проектов по отраслям» (FR-5): горизонтальные
 * столбики по убыванию с переключателями «План / Факт» и
 * «Сумма / Кол-во» (FR-5.2).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fmt, moneyMln } from './format';
import { SegToggle } from './SegToggle';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import type { ProjectsSlice } from '../../../types/projects';

type PlanFact = 'plan' | 'fact';
type SumCount = 'sum' | 'count';

interface IndustriesWidgetProps {
  industries: ProjectsSlice[];
}

export const IndustriesWidget = ({ industries }: IndustriesWidgetProps) => {
  const { t } = useTranslation();
  const [planFact, setPlanFact] = useState<PlanFact>('plan');
  const [sumCount, setSumCount] = useState<SumCount>('sum');

  const valueOf = (slice: ProjectsSlice): number => {
    if (sumCount === 'sum') {
      return (planFact === 'plan' ? slice.planSumMln : slice.factSumMln) ?? 0;
    }
    return (planFact === 'plan' ? slice.planCount : slice.factCount) ?? 0;
  };

  const valueLabel = (v: number): string => {
    if (sumCount === 'count') {
      return `${fmt(v)} ${t('projects.units.projectsShort')}`;
    }
    const money = moneyMln(v);
    return `${money.value} ${t(money.mlrd ? 'projects.units.mlrdTg' : 'projects.units.mlnTg')}`;
  };

  // Сортировка по убыванию — требование FR-5.2.
  const rows = industries
    .map((slice) => ({
      key: slice.key,
      label: t(`projects.infrastructure.industryNames.${slice.key}`, {
        defaultValue: slice.key,
      }),
      value: valueOf(slice),
    }))
    .sort((a, b) => b.value - a.value);
  const max = rows[0]?.value ?? 0;

  return (
    <Card className="shadow-card" data-testid="projects-industries">
      <CardHeader className="flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <CardTitle>{t('projects.infrastructure.industries.title')}</CardTitle>
        <div className="flex gap-2">
          <SegToggle<PlanFact>
            ariaLabel={t('projects.infrastructure.seg.planFact')}
            options={[
              { value: 'plan', label: t('projects.infrastructure.seg.plan') },
              { value: 'fact', label: t('projects.infrastructure.seg.fact') },
            ]}
            value={planFact}
            onChange={setPlanFact}
            testId="projects-industries-planfact"
          />
          <SegToggle<SumCount>
            ariaLabel={t('projects.infrastructure.seg.sumCount')}
            options={[
              { value: 'sum', label: t('projects.infrastructure.seg.sum') },
              { value: 'count', label: t('projects.infrastructure.seg.count') },
            ]}
            value={sumCount}
            onChange={setSumCount}
            testId="projects-industries-sumcount"
          />
        </div>
      </CardHeader>
      <CardContent>
        <ul className="flex flex-col gap-2.5">
          {rows.map((row) => (
            <li
              key={row.key}
              className="grid grid-cols-[minmax(7rem,11rem)_minmax(3rem,1fr)_5.5rem] items-center gap-2.5 text-sm"
              data-testid={`projects-industry-${row.key}`}
            >
              <span
                className="truncate text-text-muted"
                title={row.label}
              >
                {row.label}
              </span>
              <span className="h-4 overflow-hidden rounded bg-surface-muted">
                <span
                  className="block h-full rounded bg-chart-indigo"
                  style={{
                    width: max > 0 ? `${(row.value / max) * 100}%` : 0,
                  }}
                />
              </span>
              <span className="whitespace-nowrap text-right font-semibold tabular-nums">
                {valueLabel(row.value)}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};
